import { Mail } from "lucide-react";

import { Button } from "@/components/flowly/infra/Button/Button";
import { ModalCard } from "@/components/flowly/infra/ModalCard/ModalCard";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMarketingOptIn } from "@/pages/Profile/useMarketingOptIn";

/**
 * One-shot consent prompt shown to brand-new Google-OAuth signups right after
 * the redirect lands. Email-signup users never see this — they consented (or
 * declined) inline on the signup form, which writes through to the DB trigger
 * via raw_user_meta_data.
 *
 * The "Join" path runs the same opt-in flow as the Profile-page nudge so the
 * audit trail (consent boolean + timestamp + IP + text version) is identical
 * regardless of where the user agreed. Dismissal — via either button or the
 * X — sets a per-user localStorage flag in AuthContext.dismissMarketingPrompt
 * so the modal never re-triggers for that user on that device.
 */
export function MarketingConsentModal() {
  const { t } = useLanguage();
  const { marketingPromptOpen, dismissMarketingPrompt } = useAuth();
  const { isSaving, optIn } = useMarketingOptIn();

  const handleJoin = async () => {
    await optIn();
    dismissMarketingPrompt();
  };

  return (
    <ModalCard
      isOpen={marketingPromptOpen}
      onClose={dismissMarketingPrompt}
      maxWidth="max-w-md"
      dismissible={false}
    >
      <div className="flex flex-col items-start gap-4 text-start">
        <div className="rounded-full bg-brand p-3">
          <Mail className="w-5 h-5 text-brand-foreground" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">
            {t.profile.marketingNudgeTitle}
          </h2>
          <p className="text-sm text-muted-foreground mt-1.5 leading-snug">
            {t.profile.marketingNudgeSubtitle}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
          <Button
            variant="accent"
            size="sm"
            onClick={handleJoin}
            disabled={isSaving}
            className="w-full sm:flex-1"
          >
            {t.profile.marketingNudgeCta}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismissMarketingPrompt}
            disabled={isSaving}
            className="w-full sm:flex-1"
          >
            {t.auth.marketingPromptDecline}
          </Button>
        </div>
      </div>
    </ModalCard>
  );
}
